import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

export enum ThemeModeEnum {
  Light = "light",
  Dark = "dark",
}

const STORAGE_KEY = "powur:theme-mode";

type ThemeModeContextT = {
  mode: ThemeModeEnum;
  setMode: (mode: ThemeModeEnum) => void;
  toggleMode: () => void;
};

const ThemeModeContext = createContext<ThemeModeContextT | undefined>(undefined);
ThemeModeContext.displayName = "ThemeModeContext";

type ThemeModeProviderPropsT = {
  children: ReactNode;
};

const applyMode = (mode: ThemeModeEnum) => {
  document.documentElement.setAttribute("data-theme", mode);
};

const readStoredMode = (): ThemeModeEnum => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === ThemeModeEnum.Dark) {
    return ThemeModeEnum.Dark;
  }
  return ThemeModeEnum.Light;
};

export const ThemeModeProvider = ({ children }: ThemeModeProviderPropsT) => {
  const [mode, setModeState] = useState<ThemeModeEnum>(() => {
    const initial = readStoredMode();
    applyMode(initial);
    return initial;
  });

  useEffect(() => {
    applyMode(mode);
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const setMode = (next: ThemeModeEnum) => {
    setModeState(next);
  };

  const toggleMode = () => {
    setModeState((prev) => {
      return prev === ThemeModeEnum.Light ? ThemeModeEnum.Dark : ThemeModeEnum.Light;
    });
  };

  return <ThemeModeContext.Provider value={{ mode, setMode, toggleMode }}>{children}</ThemeModeContext.Provider>;
};

export const useThemeMode = (): ThemeModeContextT => {
  const context = useContext(ThemeModeContext);
  if (context === undefined) {
    throw new Error("useThemeMode must be used within a ThemeModeProvider");
  }
  return context;
};
